import React, { useEffect, useState, useRef } from 'react';
import { CircuitBoardIcon, BrainCircuitIcon, BarChart3Icon, DatabaseIcon, ExternalLinkIcon, LinkedinIcon } from 'lucide-react';
export const TeamMembers = ({
  onCursorChange
}) => {
  const containerRef = useRef(null);
  const memberRefs = useRef([]);
  useEffect(() => {
    // Animation on scroll
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Stagger team member animations
          memberRefs.current.forEach((member, index) => {
            if (member) {
              setTimeout(() => {
                member.classList.add('animate-in');
              }, index * 200);
            }
          });
        }
      });
    }, {
      threshold: 0.2
    });
    // Observe the section
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);
  const teamMembers = [{
    name: 'Cheryl Qian',
    title: 'Chief Technology Officer',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/kJRNTmXBtEQZyBVkZnczKs/Cheryl.jpg',
    bio: 'Pioneering the fusion of synthetic biology and computational systems. Leading our technical vision with expertise in neural interfaces and biological distortion.',
    stats: {
      innovation: '97%',
      neuralSync: '99.2%',
      techVision: 'OPTIMAL'
    },
    icon: <BrainCircuitIcon className="w-5 h-5" data-id="element-1323" />
  }, {
    name: 'Steve Jobs',
    title: 'Chief Marketing Officer',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mrB57QHL8orBCSk6DTgacX/Steve.jpg',
    bio: 'Visionary strategist crafting our market presence. Transforming complex biological concepts into compelling narratives that resonate across industries.',
    stats: {
      influence: '95%',
      strategy: 'EXCEPTIONAL',
      marketPenetration: '92.7%'
    },
    icon: <BarChart3Icon className="w-5 h-5" data-id="element-1324" />
  }, {
    name: 'Sarah Wynn-Williams',
    title: 'Chief Information Officer',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hKm3K3Uusc2q7zb5oWK3YG/Sarah.jpg',
    bio: 'Architecting our digital infrastructure with expertise in quantum computing and secure data systems. Ensuring seamless integration of biological and digital realms.',
    stats: {
      dataIntegrity: '98.3%',
      systemSecurity: 'FORTIFIED',
      quantumEfficiency: 'HIGH'
    },
    icon: <DatabaseIcon className="w-5 h-5" data-id="element-1325" />
  }];
  return <section id="team" ref={containerRef} className="w-full py-24 text-[#e0e6f0] relative overflow-hidden" data-id="element-1326">
      <style jsx global data-id="element-1327">{`
        @keyframes team-member-reveal {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .team-member {
          opacity: 0;
          transform: translateY(30px);
        }
        .team-member.animate-in {
          animation: team-member-reveal 0.6s
            cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
        .member-image-container {
          position: relative;
          overflow: hidden;
          transition: all 0.5s ease;
        }
        .member-image-container:hover .member-image {
          transform: scale(1.05);
        }
        .member-image {
          transition: all 0.5s ease;
        }
        .scan-line {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(111, 248, 240, 0.8),
            transparent
          );
          animation: scan 2s linear infinite;
          opacity: 0;
          z-index: 5;
        }
        .member-image-container:hover .scan-line {
          opacity: 1;
        }
        @keyframes scan {
          0% {
            top: 0%;
          }
          100% {
            top: 100%;
          }
        }
        .status-indicator {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 2px 8px;
          border-radius: 12px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          background: rgba(94, 231, 223, 0.1);
          color: #5ee7df;
        }
        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #5ee7df;
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(94, 231, 223, 0.7);
          }
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 6px rgba(94, 231, 223, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(94, 231, 223, 0);
          }
        }
        .member-stats {
          transition: all 0.3s ease;
          opacity: 0;
          transform: translateY(10px);
        }
        .team-member:hover .member-stats {
          opacity: 1;
          transform: translateY(0);
        }
        .corner-accent {
          position: absolute;
          width: 15px;
          height: 15px;
          border-color: #5ee7df;
          opacity: 0.7;
          transition: all 0.3s ease;
        }
        .team-member:hover .corner-accent {
          width: 20px;
          height: 20px;
          opacity: 1;
        }
      `}</style>
      {/* Background effects */}
      <div className="absolute inset-0 bg-[#0a0e17]" data-id="element-1328"></div>
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay" data-id="element-1329"></div>
      <div className="container mx-auto px-6 relative z-10" data-id="element-1330">
        <div className="text-center mb-16" data-id="element-1331">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gradient font-heading" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1332">
            EXECUTIVE TEAM
          </h2>
          <div className="h-px w-20 bg-gradient-to-r from-[#5ee7df] to-[#2a4d7f] mx-auto mb-6" data-id="element-1333"></div>
          <p className="text-[#8a9cb9] max-w-2xl mx-auto font-mono" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1334">
            The architects of our biological revolution. Merging expertise
            across technology, marketing, and information systems.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1335">
          {teamMembers.map((member, index) => <div key={index} ref={el => memberRefs.current[index] = el} className="team-member glass p-6 transition-all duration-500 hover:bg-[#0a0e17]/80 group relative border border-[#1a2a40]/50 hover:border-[#5ee7df]/50 rounded-lg" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-1336">
              {/* Glow effect on hover */}
              <div className="absolute -inset-0.5 bg-gradient-to-br from-[#5ee7df]/30 to-[#2a4d7f]/30 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500 -z-10 rounded-lg" data-id="element-1337"></div>
              {/* Corner accents */}
              <div className="corner-accent top-0 left-0 border-t-2 border-l-2" data-id="element-1338"></div>
              <div className="corner-accent top-0 right-0 border-t-2 border-r-2" data-id="element-1339"></div>
              <div className="corner-accent bottom-0 left-0 border-b-2 border-l-2" data-id="element-1340"></div>
              <div className="corner-accent bottom-0 right-0 border-b-2 border-r-2" data-id="element-1341"></div>
              {/* Image with effects */}
              <div className="member-image-container mb-6 rounded-lg overflow-hidden" data-id="element-1342">
                <img src={member.image} alt={member.name} className="member-image w-full h-64 object-cover object-center filter grayscale hover:grayscale-0 contrast-125" data-id="element-1343" />
                <div className="scan-line" data-id="element-1344"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e17] via-transparent to-transparent opacity-50" data-id="element-1345"></div>
                <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(16,22,36,0.2)_50%)] bg-[size:100%_4px] opacity-20 transition-opacity duration-500" data-id="element-1346"></div>
              </div>
              {/* Member info */}
              <div className="flex justify-between items-start mb-3" data-id="element-1347">
                <div data-id="element-1348">
                  <h3 className="text-xl font-bold text-[#5ee7df] font-heading" data-id="element-1349">
                    {member.name}
                  </h3>
                  <div className="status-indicator mt-1" data-id="element-1350">
                    <div className="status-dot" data-id="element-1351"></div>
                    <span data-id="element-1352">{member.title}</span>
                  </div>
                </div>
                <div className="p-2 bg-[#5ee7df]/10 rounded-full" data-id="element-1353">
                  {member.icon}
                </div>
              </div>
              <p className="text-[#a0c0e0] text-sm mb-4 font-mono" data-id="element-1354">
                {member.bio}
              </p>
              {/* Member stats - revealed on hover */}
              <div className="member-stats mt-4 pt-4 border-t border-[#1a2a40]/50" data-id="element-1355">
                <div className="text-xs text-[#5ee7df] font-mono mb-2" data-id="element-1356">
                  PERFORMANCE METRICS
                </div>
                <div className="grid grid-cols-2 gap-2" data-id="element-1357">
                  {Object.entries(member.stats).map(([key, value], i) => <div key={i} className="text-xs text-[#a0c0e0] font-mono p-2 bg-[#1a2a40]/30 rounded flex justify-between" data-id="element-1358">
                      <span data-id="element-1359">{key}:</span>
                      <span className="text-[#e64cff]" data-id="element-1360">{value}</span>
                    </div>)}
                </div>
                <div className="mt-4 flex justify-end" data-id="element-1361">
                  <a href="#" className="text-xs text-[#5ee7df] font-mono flex items-center gap-1 hover:underline" data-id="element-1362">
                    <span data-id="element-1363">FULL PROFILE</span>
                    <ExternalLinkIcon size={12} data-id="element-1364" />
                  </a>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};